import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Eye, Pencil, Trash2, Download, Upload, Copy } from 'lucide-react';
import { useT } from '@/store/i18n';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Card } from '@/components/ui/Card';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { confirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/components/ui/Toast';
import { QuestionDetailModal } from '@/components/QuestionDetailModal';
import { ImportQuestionsModal } from '@/components/ImportQuestionsModal';
import { PageHeader } from '@/components/ui/PageHeader';
import { downloadSingleQuestion } from '@/lib/export';
import { api } from '@/lib/api';
import type { Question, SubjectGroup, GradeLevel, QuestionStatus } from '@/types';

interface QuestionsPayload {
  questions?: Question[];
  pagination?: { total?: number; page?: number; pages?: number };
}
interface QuestionsResponse extends QuestionsPayload {
  data?: QuestionsPayload;
}

interface MetaResponse {
  data?: { subject_groups?: SubjectGroup[]; grade_levels?: GradeLevel[] };
  subject_groups?: SubjectGroup[];
  grade_levels?: GradeLevel[];
}

export function QuestionsPage() {
  const navigate = useNavigate();
  const t = useT();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [subject, setSubject] = useState('');
  const [grade, setGrade] = useState('');
  const [status, setStatus] = useState<QuestionStatus | ''>('');
  const [page, setPage] = useState(1);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null);

  const meta = useQuery({
    queryKey: ['metadata'],
    queryFn: () => api<MetaResponse>('metadata/all.php').then((r) => r.data ?? r),
    staleTime: 5 * 60_000,
  });

  const subjects = meta.data?.subject_groups ?? [];
  const grades = meta.data?.grade_levels ?? [];

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    if (subject) p.set('subject_group_id', subject);
    if (grade) p.set('grade_level_id', grade);
    if (status) p.set('status', status);
    p.set('page', String(page));
    p.set('limit', '20');
    return p.toString();
  }, [search, subject, grade, status, page]);

  const remove = useMutation({
    mutationFn: (id: number) => api(`questions/manage.php?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success(t('common.delete_success'));
      qc.invalidateQueries({ queryKey: ['questions'] });
      qc.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
    onError: (e: Error) => toast.error(t('error.delete_failed'), e.message),
  });

  async function handleDuplicate(id: number) {
    setDuplicatingId(id);
    toast.info(t('q.duplicate_copying'));
    try {
      const r = await api<{ data?: { question?: any } } & { question?: any }>(
        `questions/manage.php?id=${id}`
      );
      const q = r.data?.question ?? r.question ?? r.data;
      if (!q) throw new Error('not found');
      localStorage.setItem(
        'baiboon_v8_wizard_clone',
        JSON.stringify({ ...q, id: undefined, status: 'draft' })
      );
      navigate('/questions/new');
    } catch (e) {
      toast.error(t('q.duplicate_fail'), (e as Error).message);
    } finally {
      setDuplicatingId(null);
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['questions', params],
    queryFn: () =>
      api<QuestionsResponse>(`questions/index.php?${params}`).then(
        (r) => r.data ?? r
      ),
  });

  const questions = data?.questions ?? [];
  const total = data?.pagination?.total ?? questions.length;

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('q.title')}
        description={`${t('q.subtitle')} · ${t('common.total')} ${total.toLocaleString('th-TH')} ${t('units.questions')}`}
        actions={
          <>
            <Button
              variant="secondary"
              leftIcon={<Upload className="w-4 h-4" />}
              onClick={() => setImportOpen(true)}
            >
              {t('q.import_csv')}
            </Button>
            <Button
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => navigate('/questions/new')}
            >
              {t('q.create_new')}
            </Button>
          </>
        }
      />

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Input
            leftIcon={<Search className="w-4 h-4" />}
            placeholder={t('q.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select value={subject} onChange={(e) => setSubject(e.target.value)}>
            <option value="">{t('filter.all_subjects')}</option>
            {subjects.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Select>
          <Select value={grade} onChange={(e) => setGrade(e.target.value)}>
            <option value="">{t('filter.all_grades')}</option>
            {grades.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </Select>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as QuestionStatus | '')}
          >
            <option value="">{t('filter.all_status')}</option>
            <option value="draft">{t('status.draft')}</option>
            <option value="pending">{t('status.pending')}</option>
            <option value="approved">{t('status.approved')}</option>
            <option value="rejected">{t('status.rejected')}</option>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <th className="text-left font-semibold px-5 py-3">{t('q.col.question')}</th>
                <th className="text-left font-semibold px-5 py-3 hidden md:table-cell">{t('q.col.subject')}</th>
                <th className="text-left font-semibold px-5 py-3 hidden lg:table-cell">{t('q.col.grade')}</th>
                <th className="text-left font-semibold px-5 py-3">{t('q.col.status')}</th>
                <th className="text-left font-semibold px-5 py-3 hidden lg:table-cell">{t('q.col.created')}</th>
                <th className="text-right font-semibold px-5 py-3">{t('q.col.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={6} className="px-5 py-4">
                      <Skeleton className="w-full h-4" />
                    </td>
                  </tr>
                ))
              ) : questions.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState
                      title={t('q.empty')}
                      description={t('q.empty_desc')}
                      action={{
                        label: t('q.create_new'),
                        onClick: () => navigate('/questions/new'),
                      }}
                    />
                  </td>
                </tr>
              ) : (
                questions.map((q) => (
                  <tr key={q.id} className="hover:bg-slate-50/70 transition-colors">
                    <td className="px-5 py-3 max-w-xs">
                      <div className="text-slate-900 font-medium truncate">
                        {q.question_text || '—'}
                      </div>
                      {q.topic && (
                        <div className="text-xs text-slate-500 truncate mt-0.5">
                          {q.topic}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell">
                      {q.subject_name ? (
                        <Badge tone="brand">{q.subject_name}</Badge>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3 hidden lg:table-cell text-slate-600">
                      {q.grade_name ?? '—'}
                    </td>
                    <td className="px-5 py-3">
                      <StatusBadge status={q.status} />
                    </td>
                    <td className="px-5 py-3 hidden lg:table-cell text-slate-500 text-xs">
                      {new Date(q.created_at).toLocaleDateString('th-TH', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t('q.aria.view')}
                          onClick={() => setDetailId(q.id)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t('q.aria.edit')}
                          onClick={() => navigate(`/questions/edit/${q.id}`)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t('q.aria.duplicate')}
                          disabled={duplicatingId === q.id}
                          onClick={() => handleDuplicate(q.id)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t('q.aria.download')}
                          onClick={() => downloadSingleQuestion(q.id, 'pdf')}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t('q.aria.delete')}
                          onClick={async () => {
                            const ok = await confirmDialog({
                              title: t('q.delete_confirm'),
                              description: t('q.delete_irreversible'),
                              destructive: true,
                              confirmLabel: t('btn.delete'),
                            });
                            if (ok) remove.mutate(q.id);
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {questions.length > 0 && (
          <div className="px-4 border-t border-slate-100">
            <Pagination page={page} pageSize={20} total={total} onPageChange={setPage} />
          </div>
        )}
      </Card>

      <QuestionDetailModal questionId={detailId} onClose={() => setDetailId(null)} />
      <ImportQuestionsModal open={importOpen} onClose={() => setImportOpen(false)} />
    </div>
  );
}
